import { History, MessageSquare, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * The permanent 40px icon rail. Always mounted; the panel opens to its left.
 *
 * Rendered from a list rather than hard-coded so a future section is an array entry, which is the reason the
 * design chose icons over a labelled tab strip in the first place.
 */
export type PanelSection = 'activity' | 'comments';

interface SectionDescriptor {
  id: PanelSection;
  icon: LucideIcon;
  /** Doubles as the tooltip text and the aria-label — one string, so they cannot drift apart. */
  label: string;
  comingSoon?: boolean;
}

/** Copy is verbatim from the design handoff §6. Do not paraphrase; it was written to read as a sentence. */
const SECTIONS: SectionDescriptor[] = [
  { id: 'activity', icon: History, label: 'Activity log' },
  { id: 'comments', icon: MessageSquare, label: 'Comments — coming soon', comingSoon: true },
];

interface ActivitySectionRailProps {
  /** The open section, or null when the panel is collapsed. */
  active: PanelSection | null;
  /** Clicking the ACTIVE item collapses; clicking an inactive one opens on that section. */
  onSelect: (section: PanelSection) => void;
}

export function ActivitySectionRail({ active, onSelect }: ActivitySectionRailProps) {
  return (
    <nav
      aria-label="Record panels"
      className="flex w-10 shrink-0 flex-col items-center gap-0.5 border-l border-border bg-card py-2"
    >
      {SECTIONS.map(({ id, icon: Icon, label, comingSoon }) => {
        const isActive = active === id;

        return (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                // aria-pressed rather than aria-expanded: the button is a TOGGLE whose state is "this
                // section is showing", and the region it controls is a sibling, not its own content.
                aria-pressed={isActive}
                aria-label={label}
                onClick={() => onSelect(id)}
                className={cn(
                  'relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  comingSoon && 'opacity-75',
                )}
              >
                <Icon className="h-[17px] w-[17px]" aria-hidden />
                {comingSoon && (
                  // The 5px "there is something here later" dot. Decorative — the tooltip and the
                  // aria-label already say "coming soon", so announcing it twice would be noise.
                  <span
                    aria-hidden
                    className="absolute right-1 top-1 h-[5px] w-[5px] rounded-full bg-info"
                  />
                )}
              </button>
            </TooltipTrigger>
            {/* Opens LEFT: the rail is flush against the viewport edge, so any other side clips. */}
            <TooltipContent side="left">{label}</TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );
}
